import React, { useEffect, useMemo, useState } from "react";

import Input from "src/components/ui/Input";
import Button from "src/components/ui/Button";
import { useAppDispatch, useAppSelector } from "src/redux/hooks";
import * as tenantActions from "src/redux/slices/tenantSlice";
import useWarehouseLabel from "src/hooks/useWarehouseLabel";
import { toast } from "react-toastify";

// Grouped to mirror the sidebar's menu/submenu structure, so a module that
// lives under e.g. "Purchases" in the menu (Suppliers) is grouped with it
// here too, instead of an arbitrary flat list. "Dashboard" is intentionally
// left out — it's always visible to every signed-in user, there's nothing
// to gate.
const MODULE_GROUPS: { group: string; modules: { key: string; label: string }[] }[] = [
  {
    group: "Catalog & Inventory",
    // One row per sidebar item under Inventory — previously "Warehouses",
    // "Stock Levels", "Stock Adjustment", "Stock Movements" and "Transfers"
    // all shared a single "inventory" permission (and Categories/Brands/
    // Units shared "products"), so granting any one granted all of them.
    modules: [
      { key: "products", label: "Products" },
      { key: "categories", label: "Categories" },
      { key: "brands", label: "Brands" },
      { key: "units", label: "Units" },
      { key: "warehouses", label: "Warehouses" },
      { key: "stockLevels", label: "Stock Levels" },
      { key: "stockAdjustment", label: "Stock Adjustment" },
      { key: "stockMovements", label: "Stock Movements" },
      { key: "transfers", label: "Transfers" },
    ],
  },
  {
    group: "Sales",
    modules: [
      { key: "sales", label: "Sales" },
      { key: "customers", label: "Customers" },
    ],
  },
  {
    group: "Purchases",
    modules: [
      { key: "purchases", label: "Purchases" },
      { key: "suppliers", label: "Suppliers" },
    ],
  },
  {
    group: "Operations",
    modules: [{ key: "adjustments", label: "Adjustments" }],
  },
  {
    group: "Reports",
    // No standalone "page access" row — the Reports menu item and page show
    // up automatically once a role can view at least one report type below
    // (see menuConfig.ts's "reports" entry and ReportsPage.tsx).
    modules: [
      { key: "reportsSales", label: "Sales Report" },
      { key: "reportsPurchase", label: "Purchase Report" },
      { key: "reportsStock", label: "Stock Report" },
      { key: "reportsProfit", label: "Profit Report" },
      { key: "reportsInventoryValuation", label: "Inventory Valuation Report" },
      { key: "reportsTransfer", label: "Stock Transfer Report" },
    ],
  },
  {
    group: "Team",
    modules: [{ key: "users", label: "Staff" }],
  },
];

const ACTIONS = ["view", "create", "edit", "delete"];

interface TenantRoleFormProps {
  tenantId?: string;
  tenantRoleId?: string;
  existingRole?: any;
  isAdminAuth?: boolean;
  onCancel?: () => void;
  onSuccess?: () => void;
}

const TenantRoleForm: React.FC<TenantRoleFormProps> = ({
  tenantId,
  tenantRoleId,
  existingRole,
  isAdminAuth = false,
  onCancel,
  onSuccess,
}) => {
  const dispatch = useAppDispatch();
  const { createTenantRole, editTenantRole, tenantRoleList } = useAppSelector(
    (state) => state.tenant
  );
  const { plural: warehouseLabelPlural } = useWarehouseLabel();
  const [role, setRole] = useState<{
    name: string;
    description: string;
    permissions: Record<string, string[]>;
  }>({
    name: "",
    description: "",
    permissions: {},
  });
  const [errors, setErrors] = useState({
    name: "",
    description: "",
    permissions: "",
  });
  const [requestedList, setRequestedList] = useState(false);

  const tenantIdParam = tenantId || undefined;
  const resolvedRole = useMemo(() => {
    if (existingRole) return existingRole;
    if (!tenantRoleId) return null;
    return (
      tenantRoleList?.data?.data?.find(
        (roleItem: any) => roleItem?._id === tenantRoleId
      ) || null
    );
  }, [existingRole, tenantRoleId, tenantRoleList?.data?.data]);

  useEffect(() => {
    if (!tenantRoleId || requestedList) return;
    dispatch(
      tenantActions.getTenantRoleListRequest({
        tenantId: tenantIdParam,
        isAdminAuth,
      })
    );
    setRequestedList(true);
  }, [tenantRoleId, requestedList, dispatch, tenantIdParam, isAdminAuth]);

  useEffect(() => {
    if (tenantRoleId && requestedList && !resolvedRole && tenantRoleList?.data) {
      toast.error("Role not found");
      onCancel?.();
    }
  }, [
    tenantRoleId,
    requestedList,
    resolvedRole,
    tenantRoleList?.data,
    onCancel,
  ]);

  useEffect(() => {
    if (resolvedRole) {
      setRole({
        name: resolvedRole?.name || "",
        description: resolvedRole?.description || "",
        permissions: resolvedRole?.permissions || {},
      });
      return;
    }
    setRole({ name: "", description: "", permissions: {} });
  }, [resolvedRole]);

  const handleSuccess = () => {
    dispatch(tenantActions.resetCreateTenantRole());
    dispatch(tenantActions.resetEditTenantRole());
    onSuccess?.();
  };

  useEffect(() => {
    if (createTenantRole?.data) {
      toast.success("Tenant Role created successfully");
      handleSuccess();
    } else if (createTenantRole?.error) {
      toast.error("Error while creating tenant role details");
    }
  }, [createTenantRole, onSuccess]);

  useEffect(() => {
    if (editTenantRole?.data) {
      toast.success("Tenant Role updated successfully");
      handleSuccess();
    } else if (editTenantRole?.error) {
      toast.error("Error while updating tenant role details");
    }
  }, [editTenantRole, onSuccess]);

  const togglePermission = (module: string, action: string) => {
    const updated: Record<string, string[]> = { ...role.permissions };
    updated[module] = updated[module] || [];

    if (updated[module].includes(action)) {
      updated[module] = updated[module].filter((a: string) => a !== action);
    } else {
      updated[module].push(action);
    }

    setRole({ ...role, permissions: updated });
  };

  const handleSave = () => {
    if (!role.name) setErrors({ ...errors, name: "Required!" });
    else if (!role.description)
      setErrors({ ...errors, description: "Required!" });
    else if (Object.keys(role.permissions).length === 0)
      toast.error("Select atleast one permission");

    if (
      !role.name ||
      !role.description ||
      Object.keys(role.permissions).length === 0
    )
      return;

    const payload = tenantId ? { ...role, tenantId } : role;

    if (resolvedRole) {
      dispatch(
        tenantActions.editTenantRoleRequest({
          id: resolvedRole._id,
          data: payload,
          isAdminAuth,
        })
      );
    } else {
      dispatch(
        tenantActions.createTenantRoleRequest({ ...payload, isAdminAuth })
      );
    }
  };

  const isSaving = createTenantRole.loading || editTenantRole.loading;
  const isEditing = !!resolvedRole || !!tenantRoleId;
  const cancelLabel = onCancel ? "Cancel" : "Back";

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Input
          label="Role Name"
          placeholder="e.g. Inventory Manager"
          value={role.name}
          onChange={(e) => {
            const { value } = e.target;
            setRole({ ...role, name: value });
            setErrors({ ...errors, name: !value ? "Required!" : "" });
          }}
          error={!!errors.name}
          helperText={errors.name}
        />
        <Input
          label="Description"
          placeholder="Optional description"
          value={role.description}
          onChange={(e) => {
            const { value } = e.target;
            setRole({ ...role, description: value });
            setErrors({ ...errors, description: !value ? "Required!" : "" });
          }}
          error={!!errors.description}
          helperText={errors.description}
        />
      </div>

      <div className="overflow-x-auto rounded-lg border dark:border-gray-700">
        <table className="w-full text-sm">
          <thead className="bg-gray-100 text-gray-700 dark:bg-gray-900/60 dark:text-gray-300">
            <tr>
              <th className="p-2 text-left">Module</th>
              {ACTIONS.map((a) => (
                <th key={a} className="capitalize p-2 text-center">
                  {a}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {MODULE_GROUPS.map((group) => (
              <React.Fragment key={group.group}>
                <tr className="border-t bg-gray-50 dark:border-gray-700 dark:bg-gray-800/60">
                  <td
                    colSpan={ACTIONS.length + 1}
                    className="px-2 py-1.5 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400"
                  >
                    {group.group}
                  </td>
                </tr>
                {group.modules.map(({ key, label }) => (
                  <tr key={key} className="border-t dark:border-gray-700">
                    <td className="p-2 pl-4 font-medium text-gray-700 dark:text-gray-200">
                      {key === "warehouses" ? warehouseLabelPlural : label}
                    </td>
                    {ACTIONS.map((action) => (
                      <td key={action} className="p-2 text-center">
                        <input
                          type="checkbox"
                          className="cursor-pointer"
                          checked={!!role.permissions[key]?.includes(action)}
                          onChange={() => togglePermission(key, action)}
                        />
                      </td>
                    ))}
                  </tr>
                ))}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex justify-end gap-2">
        <Button variant="outline" disabled={isSaving} onClick={onCancel}>
          {cancelLabel}
        </Button>
        <Button onClick={handleSave} isLoading={isSaving} disabled={isSaving}>
          {isEditing ? "Update Role" : "Save"}
        </Button>
      </div>
    </div>
  );
};

export default TenantRoleForm;
